import { ENV_CONFIG_PC_KEY, TOKEN_PC_KEY, USER_ID_PC_KEY } from './constants';
import { playIncomingNotifySound } from './notifySound';
import { getUnprocessedApplyCount } from '@/api/friendApply';
import router from '../router';
import { msgError } from './modal';

let globalSocket = null;
let heartbeatTimer = null;
let connectPromise = null;
let socketGeneration = 0;
const eventListeners = new Set();
const deliveredMessageIds = new Set();

function isDuplicateDeliveredFrame(msgDto) {
  if (!msgDto || Number(msgDto.action) !== 2 || msgDto.id == null || msgDto.id === '') return false;
  const key = String(msgDto.id);
  if (deliveredMessageIds.has(key)) return true;
  deliveredMessageIds.add(key);
  if (deliveredMessageIds.size > 500) {
    deliveredMessageIds.delete(deliveredMessageIds.values().next().value);
  }
  return false;
}

function retireSocket(socket) {
  if (!socket) return;
  socket.onopen = null;
  socket.onmessage = null;
  socket.onerror = null;
  socket.onclose = null;
  if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
    try { socket.close(); } catch (e) {}
  }
}

/**
 * 高可用多节点 WebSocket 并发探测引擎
 */
function probeBestWsEndpoint(urls) {
  return new Promise((resolve, reject) => {
    if (!urls || urls.length === 0) {
      return reject(new Error("WS_URLS_EMPTY"));
    }

    let isResolved = false;
    const completedClients = [];

    urls.forEach(url => {
      // 利用 WebSocket 构造函数的第二个参数——“子协议（Sec-WebSocket-Protocol）”来无感安全传递 Token
      const token = localStorage.getItem(TOKEN_PC_KEY)
      // 关键修复：此前漏传第二参数，握手请求头根本不带 Sec-WebSocket-Protocol，后端永远解析不到 token
      const wsProbe = new WebSocket(url, token);
      completedClients.push(wsProbe);

      wsProbe.onopen = () => {
        if (!isResolved) {
          isResolved = true;

          completedClients.forEach(client => {
            if (client !== wsProbe) {
              retireSocket(client);
            }
          });

          resolve({ winnerUrl: url, activeSocket: wsProbe });
        } else {
          retireSocket(wsProbe);
        }
      };

      wsProbe.onerror = () => {
        if (completedClients.filter(c => c.readyState === WebSocket.CLOSED || c.readyState === WebSocket.CLOSING).length === urls.length) {
          reject(new Error("ALL_WS_ENDPOINTS_FAILED"));
        }
      };
    });
  });
}

export const GlobalWsBus = {
  async initWebSocketConnection() {
    if (globalSocket && globalSocket.readyState === WebSocket.OPEN) return;
    if (connectPromise) return connectPromise;

    connectPromise = this.openWebSocketConnection().finally(() => {
      connectPromise = null;
    });
    return connectPromise;
  },

  async openWebSocketConnection() {
    // 动态抓取当前登录人的真实 UserId 进行建连上线
    const currentUserId = localStorage.getItem(USER_ID_PC_KEY);
    if (!currentUserId) return;

    const cachedConfig = localStorage.getItem(ENV_CONFIG_PC_KEY);
    if (!cachedConfig) return;

    const configObj = JSON.parse(cachedConfig);
    const wsUrls = configObj.NETTY_WS_URLS || [];
    const generation = ++socketGeneration;
    retireSocket(globalSocket);
    globalSocket = null;
    window.GlobalActiveWebSocketInstance = null;

    try {
      const result = await probeBestWsEndpoint(wsUrls);
      if (generation !== socketGeneration) {
        retireSocket(result.activeSocket);
        return;
      }
      globalSocket = result.activeSocket;
      window.GlobalActiveWebSocketInstance = globalSocket;

      // 身份上线首次：发送 action: 1 强原子校验绑定
      const bindPacket = {
        action: 1,
        senderId: currentUserId
      };
      globalSocket.send(JSON.stringify(bindPacket));
      this.syncFriendApplyBadge();

      globalSocket.onmessage = (event) => {
        try {
          const msgDto = JSON.parse(event.data);
          if (generation !== socketGeneration || globalSocket !== result.activeSocket) return;
          // 网络抖动重连时，同一条消息可能从旧连接和新连接各到一次
          if (isDuplicateDeliveredFrame(msgDto)) return;

          // 全局分发：通告给所有订阅了消息的组件页面（不论切到好友页、标签页、修改资料页都能在这里被监听到）
          eventListeners.forEach(callback => callback(msgDto));

          // 1. 如果长连接返回 401，说明登录信息已过期，强制清理本地缓存并跳转到登录页
          if (msgDto.action === 401) {
            console.log("[长连接中心] 服务器返回 401，说明登录信息已过期，强制清理本地缓存并跳转到登录页");
            localStorage.clear()
            msgError('您的登录信息已过期，请重新登录');
            setTimeout(() => {
              router.replace('/login').catch(() => {});
            }, 2000);
          }

          // 2. 只要长连接收到新聊天包（action:2），且【不是我自己发出去的】，说明全网有增量未读流入！
          if (msgDto.action === 2 && msgDto.senderId != currentUserId) {
            if (window.location.pathname !== '/chat') {
              // 如果用户当前没有高亮在聊天流面板上，本地磁盘未读总数自增
              let currentLocalSum = parseInt(localStorage.getItem('wl_total_msg_unread') || 0);
              localStorage.setItem('wl_total_msg_unread', String(currentLocalSum + 1));
            }
          }

          // 3. 好友申请：服务端推绝对待处理数，左侧菜单不必进入好友页
          if (msgDto.action === 9) {
            localStorage.setItem('wl_total_friend_apply_unread', String(msgDto.count || 0));
            window.dispatchEvent(new CustomEvent('global_red_dot_changed'));
          }

          // 4. 消息到达时，向全网页发出一管自愈时钟信号，指令系统内所有页面同步去刷新小红点
          if (msgDto.action === 2 || msgDto.action === 4) {
            window.dispatchEvent(new CustomEvent('global_red_dot_changed'));
          }

          // 全局网页物理振铃声卡报警
          if (msgDto.action === 2 && msgDto.senderId != currentUserId) {
            this.executeGlobalRingNotification();
          }
        } catch (err) {
          console.error("解析长连接报文异常:", err);
        }
      };

      globalSocket.onclose = () => {
        if (generation !== socketGeneration) return;
        this.stopHeartbeatMechanism();
        globalSocket = null;
        window.GlobalActiveWebSocketInstance = null;
        // 遭遇网络波动断开时，3秒后自动发起自愈重连
        setTimeout(() => this.initWebSocketConnection(), 3000);
      };

      this.startHeartbeatMechanism();
    } catch (error) {
      console.error("[长连接中心] 所有的 WebSocket 探测路由已全线崩溃，请核对网络情况", error);
    }
  },

  /**
   * 网页端/PC端标准的通知横幅弹窗与硬件声卡电子振铃（无须 App 支持，PC端专属）
   */
  executeGlobalRingNotification() {
    try {
      // 横幅静音，提示音只走所选 AAC
      if (window.Notification && Notification.permission === "granted") {
        new Notification("微聊系统新消息", {
          body: "您收到了一条新的实时聊天消息",
          icon: "/favicon.ico",
          silent: true
        });
      } else if (window.Notification && Notification.permission !== "denied") {
        Notification.requestPermission();
      }

      playIncomingNotifySound();

    } catch (err) {
      console.warn("[网页振铃] 受到浏览器未交互安全沙箱限制，声音已被强制挂起", err);
    }
  },

  startHeartbeatMechanism() {
    if (heartbeatTimer) clearInterval(heartbeatTimer);
    const currentUserId = localStorage.getItem(USER_ID_PC_KEY);
    if (!currentUserId) return;

    heartbeatTimer = setInterval(() => {
      if (globalSocket && globalSocket.readyState === WebSocket.OPEN) {
        const pingPacket = { action: 3, senderId: currentUserId };
        globalSocket.send(JSON.stringify(pingPacket));
      }
    }, 45000); // 45秒标准心跳维持
  },

  stopHeartbeatMechanism() {
    if (heartbeatTimer) clearInterval(heartbeatTimer);
  },

  // 登录或重连后补一次，覆盖离线期间漏掉的申请
  async syncFriendApplyBadge() {
    try {
      const countRes = await getUnprocessedApplyCount();
      localStorage.setItem('wl_total_friend_apply_unread', String(countRes.count || 0));
      window.dispatchEvent(new CustomEvent('global_red_dot_changed'));
    } catch (e) {}
  },

  // 供主页面与各大子弹窗组件多点订阅的核心函数
  subscribe(callback) { eventListeners.add(callback); },
  unsubscribe(callback) { eventListeners.delete(callback); }
};
